'use client';

import Link from 'next/link';
import Image from 'next/image';

const posts = [
  {
    slug: 'outsourcing-real-estate-photo-editing',
    title: 'Why Real Estate Photographers Outsource Photo Editing',
    excerpt: 'Save 15+ hours per week, cut costs, and scale your photography business faster by outsourcing your editing.',
    category: 'Business Growth',
    readTime: '10 min read',
    image: 'https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    slug: 'hdr-vs-flambient-real-estate-photography',
    title: 'HDR vs Flambient: Which Is Better for Real Estate?',
    excerpt: 'Compare HDR and flambient techniques — pros, cons, and the best use cases to choose the right approach.',
    category: 'Photography Tips',
    readTime: '12 min read',
    image: 'https://images.pexels.com/photos/1571468/pexels-photo-1571468.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    slug: 'day-to-dusk-conversions-boost-listings',
    title: 'How Day to Dusk Conversions Boost Your Listings',
    excerpt: 'Why twilight photos create emotional connections with buyers and transform your marketing strategy.',
    category: 'Image Editing',
    readTime: '6 min read',
    image: 'https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
];

const BlogSection = () => {
  return (
    <section className="relative py-20 bg-[#0B0F1A] overflow-hidden">
      {/* ambient glow */}
      <div className="absolute top-0 left-1/3 w-[800px] h-[600px] bg-purple-600/8 rounded-full blur-[150px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Heading */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-600/10 border border-purple-600/20 rounded-full mb-4">
              <span className="text-xs font-semibold text-purple-400 uppercase tracking-wider">From the Blog</span>
            </div>
            <h2 className="text-4xl font-bold text-white leading-tight">
              Insights to help you{' '}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                sell faster
              </span>
            </h2>
            <p className="text-gray-400 text-lg mt-3 max-w-xl">
              Expert tips, industry trends, and best practices for real estate photography and editing.
            </p>
          </div>
          <Link
            href="/blog"
            className="group inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/[0.04] border border-white/[0.12] text-white font-semibold hover:bg-white/[0.08] hover:border-white/30 transition-all duration-300 self-start"
          >
            View All Articles
            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>

        {/* Posts */}
        <div className="grid md:grid-cols-3 gap-6">
          {posts.map((post) => (
            <Link
              key={post.slug}
              href={`/blog/${post.slug}`}
              className="group bg-[#13151F]/80 backdrop-blur-sm rounded-2xl border border-white/[0.08] overflow-hidden hover:border-purple-500/30 hover:-translate-y-1 transition-all duration-300"
            >
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={post.image}
                  alt={post.title}
                  fill
                  sizes="(max-width: 768px) 100vw, 33vw"
                  className="object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#13151F] via-transparent to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-black/50 backdrop-blur-sm border border-white/10 text-white text-xs font-bold rounded-lg">
                    {post.category}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{post.readTime}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2 leading-snug line-clamp-2 group-hover:text-purple-300 transition-colors">
                  {post.title}
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed line-clamp-3">{post.excerpt}</p>
                <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-purple-400 group-hover:gap-2.5 transition-all">
                  Read Article
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
